import _ from 'lodash';

const PREFERENCES_KEYS = {
  AUTO_PRINT: 'AUTO_PRINT',
  STRIPE_VENUE_DEFAULT: 'STRIPE_VENUE_DEFAULT',
  VENUE_DEFAULT: 'VENUE_DEFAULT',
  ORGANIZATION_DEFAULT: 'ORGANIZATION_DEFAULT',
  RESERVATIONS_VIEW_TYPE: 'RESERVATIONS_VIEW_TYPE',
  MANAGE_PAGE: 'MANAGE_PAGE',
  MANAGE_CALENDAR_DATE_DEFAULT: 'MANAGE_CALENDAR_DATE_DEFAULT',
  MANAGE_RESERVATIONS_DEFAULT_SORT_ORDER: 'MANAGE_RESERVATIONS_DEFAULT_SORT_ORDER'
};

const PREFERENCES_PREFIX = 'Nightpro-Preferences';

class UserPreferencesService {
  constructor(localstorage, cookie, browserSupportUtils, RESERVATIONS_VIEW_TYPES, organizationService) {
    this._localstorage = localstorage;
    this._cookie = cookie;
    this._browserSupportUtils = browserSupportUtils;
    this._RESERVATIONS_VIEW_TYPES = RESERVATIONS_VIEW_TYPES;
    this._organizationService = organizationService;
  }

  get PREFERENCES_PREFIX() { //eslint-disable-line
    return PREFERENCES_PREFIX;
  }

  /**
   * @return {String}
   */
  getManagePage() {
    return sessionStorage.getItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_PAGE));
  }

  /**
   * @return {String}
   */
  setManagePage(page) {
    if (!page) return sessionStorage.removeItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_PAGE));
    return sessionStorage.setItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_PAGE), page);
  }


  /**
   * @return {String}
   */
  getManageCalendarDate() {
    return sessionStorage.getItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_CALENDAR_DATE_DEFAULT));
  }

  /**
   * @return {String}
   */
  setManageCalendarDate(date) {
    if (!date) return sessionStorage.removeItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_CALENDAR_DATE_DEFAULT));
    return sessionStorage.setItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_CALENDAR_DATE_DEFAULT), date);
  }

  /**
   * @return {String}
   */
  getManageReservationSortOrder() {
    const order = sessionStorage.getItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_RESERVATIONS_DEFAULT_SORT_ORDER));

    if (!order) return null;

    const parts = order.split(' ');

    if (!parts || !parts.length || parts.length !== 2) return null;

    return [parseInt(parts[0], 10), parts[1]];
  }

  /**
   * @return {String}
   */
  setManageReservationSortOrder(column, direction) {
    if (_.isNil(column) && !direction) return sessionStorage.removeItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_RESERVATIONS_DEFAULT_SORT_ORDER));

    return sessionStorage.setItem(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.MANAGE_RESERVATIONS_DEFAULT_SORT_ORDER), `${column} ${direction}`);
  }

  /**
   * @return {String}
   */
  getDefaultVenue() {
    return this._getKey(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.VENUE_DEFAULT));
  }

  /**
   * @param {String} viewType
   * @return {String}
   */
  setDefaultVenue(venueId) {
    if (!venueId) throw new Error('userPreferencesService - setDefaultVenue() - venueId is required');

    return this._setKey(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.VENUE_DEFAULT), venueId);
  }

  /**
   * @return {String}
   */
  getStripeVenue() {
    return this._getKey(PREFERENCES_KEYS.STRIPE_VENUE_DEFAULT);
  }

  /**
   * @param {String} viewType
   * @return {String}
   */
  setStripeVenue(venueId) {
    if (!venueId) throw new Error('userPreferencesService - setDefaultVenue() - venueId is required');

    return this._setKey(PREFERENCES_KEYS.STRIPE_VENUE_DEFAULT, venueId);
  }

  /**
   * @return {String}
   */
  getDefaultOrganization() {
    return this._getKey(PREFERENCES_KEYS.ORGANIZATION_DEFAULT);
  }

  /**
   * @param {String} organizationId
   * @return {String}
   */
  setDefaultOrganization(organizationId) {
    if (!organizationId) throw new Error('userPreferencesService - setDefaultOrganization() - organizationId is required');

    return this._setKey(PREFERENCES_KEYS.ORGANIZATION_DEFAULT, organizationId);
  }

  /**
   * @return {String}
   */
  getDefaultReservationsView() {
    return this._getKey(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.RESERVATIONS_VIEW_TYPE));
  }

  /**
   * @param {String} viewType
   * @return {String}
   */
  setDefaultReservationsView(viewType) {
    if (!viewType) throw new Error('userPreferencesService - setDefaultReservationsView() - viewType is required');

    return this._setKey(this._getKeyPrefixedByOrganization(PREFERENCES_KEYS.RESERVATIONS_VIEW_TYPE), viewType);
  }

  _getKeyPrefixedByOrganization(key) {
    if (!key) throw new Error('userPreferencesService - _getKeyPrefixedByOrganization() - key is required');

    const currentOrganization = this._organizationService.getCurrentOrganization();

    if (!currentOrganization) return null;

    return `${currentOrganization.id}-${key}`;
  }

  _getKey(key) { //eslint-disable-line
    if (!key) throw new Error('userPreferencesService - _getKey() - key is required');

    key = `${PREFERENCES_PREFIX}-${key}`;

    if (this._browserSupportUtils.cookies()) {
      return this._cookie.get(key);
    } else if (this._browserSupportUtils.localstorage()) {
      return this._localstorage.get(key);
    }
  }

  _setKey(key, value) {
    if (!key) throw new Error('userPreferencesService - _setKey() - key is required');
    if (value === null || value === undefined) throw new Error('userPreferencesService - _setKey() - value is required');

    key = `${PREFERENCES_PREFIX}-${key}`;

    if (this._browserSupportUtils.cookies()) {
      this._cookie.set(key, value);
    } else if (this._browserSupportUtils.localstorage()) {
      this._localstorage.set(key, value);
    }

    return value;
  }
}

UserPreferencesService.$inject = [
  'jsSdk.localstorage',
  'jsSdk.cookie',
  'jsSdk.browserSupportUtils',
  'RESERVATIONS_VIEW_TYPES',
  'organizationService'
];

export default UserPreferencesService;



// WEBPACK FOOTER //
// ./src/services/userPreferencesService.js